import type { A } from "ts-toolbelt"
import type {
  PaginationQuery,
  PaginatedResponse,
} from "@/utils/utilityTypes"
import { api } from "@/api"
import type { Ref } from "vue"
import { useQuery } from "@tanstack/vue-query"
import type { TransactionStatus } from "../sharedTypes/transactions"

type TransactionShared = {
  id: string
  type: "payment" | "refund"
  createdAt: Date
  status: TransactionStatus
  amountTransactedGross: number
  amountTransactedTotal: number
  source: "donation" | "sale" | "unlinked"
  sourceNumber: number | null
  sourceId: string
  currency: string
}

export type TransactionCard = A.Compute<
  TransactionShared & {
    transactionMethod: "pos_card"
    cardScheme: string
    cardNumberLast4: string
  }
>

export type TransactionCash = A.Compute<
  TransactionShared & {
    transactionMethod: "pos_cash"
  }
>

export type Transaction = TransactionCard | TransactionCash

export type TransactionQuery = A.Compute<
  PaginationQuery &
    Partial<{
      date: Date
    }>
>

export const listTransactions = async (
  query: TransactionQuery
) => {
  const { body } = await api.get<
    PaginatedResponse<
      RecursivelyConvertDatesToStrings<Transaction>
    >
  >("/transactions/", {
    query,
  })

  return {
    ...body,
    items: body.items.map((transaction) => ({
      ...transaction,
      createdAt: DateFns.parseISO(transaction.createdAt),
    })),
  }
}

export const useTransactions = (
  query: TransactionQuery | Ref<TransactionQuery>
) =>
  useQuery({
    queryKey: [BASE_TRANSACTIONS_QUERY_KEY, query] as const,
    queryFn: ({ queryKey: [_, query_] }) =>
      listTransactions(query_),
    refetchInterval: 60 * 1000,
    staleTime: 0,
  })
